/**
 * TEMP-H-001/DV-TEMP-005: useQcSummary Hook
 *
 * React Query hook for fetching QC summary with persona, rubric, and scores.
 * Updated for DV-TEMP-005 to use demo endpoints with proper headers.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { buildApiUrl, API_ENDPOINTS, getApiHeadersGet } from '@/lib/api-base';
import { executionKeys, type QCSummary, APIClientError } from '@/lib/api-types';
import type { StageType } from '@/types/execution';

interface UseQcSummaryOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export function useQcSummary(
  executionId: string,
  stage: StageType,
  options: UseQcSummaryOptions = {}
): UseQueryResult<QCSummary, APIClientError> {
  const { enabled = true, refetchInterval } = options;

  return useQuery({
    queryKey: executionKeys.qcSummary(executionId, stage),
    queryFn: async (): Promise<QCSummary> => {
      // DV-TEMP-005: Use demo endpoint with preflight-safe headers
      const url = buildApiUrl(API_ENDPOINTS.demo.qcSummary(executionId, stage));

      const response = await fetch(url, {
        credentials: 'include',
        headers: getApiHeadersGet(), // No Content-Type for GET requests
        cache: 'no-store', // API-first policy
      });

      // Capture X-Request-Id for correlation
      const requestId = response.headers.get('X-Request-Id');

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`
        }));

        // Enhanced debugging information
        const debugInfo = {
          url: url,
          method: 'GET',
          executionId,
          stage,
          status: response.status,
          statusText: response.statusText,
          requestId,
          params: { execution_id: executionId, stage },
          timestamp: new Date().toISOString()
        };

        throw new APIClientError(
          error.message || `Failed to fetch QC summary`,
          response.status,
          error.code,
          { ...error.details, ...debugInfo }
        );
      }

      const data = await response.json();

      // Attach request ID to response for logging/debugging
      if (requestId && typeof data === 'object') {
        data._requestId = requestId;
      }

      return data;
    },
    enabled: enabled && !!executionId && !!stage,
    refetchInterval,
    staleTime: 30 * 1000, // 30 seconds - QC scores update frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (no QC data yet) or 403 (unauthorized)
      if (error?.status === 404 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Utility hook to get persona information from QC summary
 */
export function usePersonaInfo(executionId: string, stage: StageType) {
  const { data: qcSummary, ...queryResult } = useQcSummary(executionId, stage);
  
  return {
    ...queryResult,
    personaSlug: qcSummary?.personaSlug,
    rubricHash: qcSummary?.rubricHash,
    weightedScore: qcSummary?.weightedScore,
    decision: qcSummary?.decision,
  };
}

export default useQcSummary;